import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { IProductDataV3 } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { App } from '@df/core/app';
import { GoogleMaps } from '@df/module-google-maps/google-maps.namespace';
import { GoogleMapsService } from '@df/module-google-maps/google-maps.service';
import { ShellComponent } from '@df/ui/common/shell/shell.component';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import sum from 'lodash-es/sum';
import { EcologiBannerBlockComponent } from '../../../basket/ecologi-banner/ecologi-banner-block.component';
import { GoogleMapComponent } from '../../../module/google-maps/google-map.component';
import { MentionMeWrapperComponent } from '../../../module/mention-me/mention-me-wrapper.component';
import { SizebayService } from '../../../module/sizebay/sizebay.service';
import { IfStoreDirective } from '../../../multisite/if-store.directive';
import { ActionComponent } from '../../../ui/api/action.component';
import { ShortAddressComponent } from '../../../ui/display/short-address/short-address.component';
import { InputWrapBodyDirective } from '../../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../../ui/form/input-wrap.component';
import { ResultComponent } from '../../../ui/resut/result.component';
import { CheckoutSuccessComponent } from '../checkout-success.component';
import { CheckoutSuccessItemComponent } from '../item/checkout-success-item.component';
import { CheckoutSuccessTotalsSectionComponent } from '../totals-section/checkout-success-totals-section.component';

export namespace NCheckoutSuccessLayout {
    export const BLOCK_ID = 'success-page-layout';

    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    export interface IBlockData extends Catalog.IBlockData<IBlockDataContent> {}

    export interface IBlockDataContent {
        signUpTitle?: string;
        signUpDescription?: string;
        signUpElements?: { title: string }[];
        signUpCopy?: string;
        returnPolicyTitle?: string;
        returnPolicyContent?: string;
        contactTel?: string;
        contactEmail?: string;
        bottomComponents?: NICmsContentComponent.IData[];
    }
}

declare global {
    interface Window {
        _mktz?: any[];
    }
}

@Component({
    selector: 'checkout-success-layout',
    templateUrl: './checkout-success-layout.html',
    styleUrls: ['./checkout-success-layout.scss'],
    standalone: true,
    imports: [
        ActionComponent,
        CheckoutSuccessItemComponent,
        CheckoutSuccessTotalsSectionComponent,
        DfUiModule,
        EcologiBannerBlockComponent,
        FormsModule,
        GoogleMapComponent,
        ICmsClientModule,
        InputWrapComponent,
        InputWrapBodyDirective,
        IfStoreDirective,
        MentionMeWrapperComponent,
        NgTemplateOutlet,
        ResultComponent,
        RouterLink,
        ShellComponent,
        ShortAddressComponent,
        SizeClassDirective,
        TranslationModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<CheckoutSuccessLayoutComponent>({
    id: NCheckoutSuccessLayout.BLOCK_ID,
    block: {
        url: NCheckoutSuccessLayout.BLOCK_ID
    },
    onDemandOnly: true,
    name: 'Success page layout',
    description: 'Successs page layout block',
    fields: [
        {
            type: NICmsContentField.EFieldType.text,
            key: 'signUpTitle',
            title: 'Sign up title',
            default: 'Save your info for next time',
            required: true
        },
        {
            type: NICmsContentField.EFieldType.textarea,
            key: 'signUpDescription',
            title: 'Sign up description',
            default: 'Create an account and we’ll save your delivery and billing information for a faster checkout next time.',
            required: true
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'signUpElements',
            title: 'Sign up elements',
            itemName: 'Element title',
            itemNameField: 'title',
            fields: [
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'title',
                    title: 'Title'
                }
            ],
            defaultSize: 3
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'signUpCopy',
            title: 'Sign up copy',
            default: 'By creating an account you’re agreeing to our terms of use and privacy policy'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'returnPolicyTitle',
            title: 'Return policy title',
            default: 'Return Policy'
        },
        {
            type: NICmsContentField.EFieldType.textarea,
            key: 'returnPolicyContent',
            title: 'Return policy content'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'contactTel',
            title: 'Contact phone number'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'contactEmail',
            title: 'Contact email'
        },
        <NICmsContentField.IComponentsField>{
            key: 'bottomComponents',
            type: NICmsContentField.EFieldType.components,
            title: 'Bottom components',
            itemName: 'component',
            default: []
        }
    ]
})
export class CheckoutSuccessLayoutComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NCheckoutSuccessLayout.IBlockDataContent;

    protected ecologiTreeNumber?: number;
    protected markers?: GoogleMaps.IMarkerOptions[];
    protected displayPlaceholder = true;
    private sizebayService = inject(SizebayService);
    constructor(
        private checkoutSuccessComponent: CheckoutSuccessComponent,
        private googleMapsService: GoogleMapsService,
        private productResource: ProductResource,
        private app: App
    ) {
        super();
    }

    override ngOnInit() {
        if (this.app.isPlatformBrowser) {
            this.loadGeolocation();
            this.prepProducts();
        }
        if (this.checkoutData && this.checkoutData.number && this.checkoutData.items) {
            this.sizebayService.orderPlacementCall(this.checkoutData.number, this.checkoutData.items);
        }
        super.ngOnInit();
    }

    protected register(): void {
        this.checkoutSuccessComponent.register();
    }

    private prepProducts(): void {
        if (this.checkoutData?.items?.length) {
            this.productResource
                .loadAllIds(
                    this.checkoutData.items.map(item => item.product_id),
                    2,
                    { safe: true }
                )
                .then(products => {
                    if (this.checkoutData.tracking && window._mktz) {
                        window._mktz.push([
                            '_Goal',
                            'sale',
                            this.checkoutData.totals.grand_total,
                            { transaction: this.checkoutData.number, skus: products.map(product => product.data?.sku) }
                        ]);
                    }
                    this.ecologiTreeNumber = sum(
                        this.checkoutData.items?.map(
                            item =>
                                ((this.productResource.getExisting(item.product_id)?.data as IProductDataV3)?.ecologi || 0) *
                                item.qty_ordered
                        )
                    );
                    this.changeDetectorRef.detectChanges();
                });
        }
    }

    private loadGeolocation(): void {
        const shippingAddress = this.checkoutData['shipping'] || {};
        this.googleMapsService
            .geocode({
                address: `${shippingAddress.street_1}, ${shippingAddress.street_2}, ${shippingAddress.city},  ${shippingAddress.region}, ${shippingAddress.postcode}}`,
                country: shippingAddress.country_id
            })
            .then(response => {
                if (response?.length && response[0].latitude && response[0].longitude) {
                    this.markers = [
                        {
                            position: {
                                lat: response[0].latitude,
                                lng: response[0].longitude
                            },
                            clickable: false,
                            center: true
                        } as GoogleMaps.IMarkerOptions
                    ];

                    this.detectChanges();
                }
            });
    }

    protected updatePlaceholder(): void {
        if (this.displayPlaceholder !== !this.registrationData?.password) {
            this.displayPlaceholder = !this.registrationData?.password;
            this.changeDetectorRef.detectChanges();
        }
    }

    protected print() {
        window.print();
    }

    get checkoutData() {
        return this.checkoutSuccessComponent?.data;
    }

    get canRegister() {
        return this.checkoutSuccessComponent.canRegister;
    }

    get status() {
        return this.checkoutSuccessComponent.status;
    }

    get registrationData() {
        return this.checkoutSuccessComponent.registrationData;
    }
}
